/**
 * A GSTIN, checked: its shape and its mod-36 check character (D12 of
 * docs/plans/report-templates.md). Nothing checked the checksum before this.
 *
 * Fifteen characters: the two-digit state code, the holder's ten-character PAN,
 * an entity number (1 to 9, then A to Z), a default `Z` or another letter or
 * digit, and the check character. A valid GSTIN gives the business's `state`
 * (its first two digits) and its `pan` (characters 3 to 12), so neither is
 * typed twice.
 */
import { gstStateOf } from './gst-states';

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const SHAPE = /^(\d{2})([A-Z]{5}\d{4}[A-Z])([1-9A-Z])([0-9A-Z])([0-9A-Z])$/;

/** A PAN: five letters, four digits, a letter. */
export const PAN_SHAPE = /^[A-Z]{5}\d{4}[A-Z]$/;

/**
 * The check character of the first fourteen characters: each character's
 * value, weighted 1 and 2 in turn, with every product's base-36 digits summed.
 * Null when the text is shorter than fourteen or holds a character outside
 * 0-9 and A-Z.
 */
export const gstinCheckChar = (first14: string): string | null => {
  if (first14.length < 14) return null;
  let sum = 0;
  for (let i = 0; i < 14; i += 1) {
    const value = ALPHABET.indexOf(first14[i] as string);
    if (value < 0) return null;
    const product = value * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return ALPHABET[(36 - (sum % 36)) % 36] as string;
};

/** What a person typed, made into the form a GSTIN is stored in: upper case, no spaces or dashes. */
export const tidyGstin = (text: string): string => text.toUpperCase().replace(/[\s.-]/g, '');

export type ParsedGstin = { readonly gstin: string; readonly state: string; readonly pan: string };

/**
 * The GSTIN, its state code and its PAN, or null when the shape, the state
 * code or the check character is wrong.
 */
export const parseGstin = (text: string): ParsedGstin | null => {
  const gstin = tidyGstin(text);
  const match = SHAPE.exec(gstin);
  if (match === null) return null;
  const state = match[1] as string;
  if (gstStateOf(state) === null) return null;
  if (gstinCheckChar(gstin) !== gstin[14]) return null;
  return { gstin, state, pan: match[2] as string };
};

/** Why a GSTIN is refused: its shape, its state code, or only its check character. */
export const gstinProblem = (text: string): 'shape' | 'state' | 'check' | null => {
  const gstin = tidyGstin(text);
  const match = SHAPE.exec(gstin);
  if (match === null) return 'shape';
  if (gstStateOf(match[1] as string) === null) return 'state';
  return gstinCheckChar(gstin) === gstin[14] ? null : 'check';
};
